/**
 * KisiPanel — geçici kişi paneli (herkese açık sayfa, backlog #646; backend #644 / #645). Rota: /kisi-panel ve /kisi-panel/<anahtar>.
 *
 * Telefonda SMS bağlantısından açılır, bu yüzden telefon öncelikli: tek sütun, büyük düğmeler, personel kabuğu (kenar çubuğu / üst menü) YOK ve personel API'sine hiçbir çağrı yok.
 *   · Bağlantıdaki anahtar BİR KEZ okunur, bellekte tutulur ve adres çubuğundan hemen silinir (history.replaceState); yenilemeden sonra tekrar gerekmez, oturum sürer.
 *   · Oturum yalnızca bellekte + sessionStorage'da (localStorage'a asla), etkinliksiz kalınca (sunucunun söylediği süre, yoksa 15 dk) kapanır ve giriş ekranına dönülür.
 *   · Kişisel veri gösteren bir sayfa: <meta name="referrer" content="no-referrer"> ve no-store (gerçek başlıklar public/.htaccess'te; /kisi-panel yoluna özel).
 *   · Dil: seçilmemişse tarayıcının dili (Türkçe değilse İngilizce); girişten sonra backend kişinin dilini söylerse (yabancı firmanın çalışanı İngilizce) ve kişi dili elle seçmediyse o kullanılır.
 * Giriş başarısızlığı her durumda tek metindir (bkz. KisiPanelGiris).
 */
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, CardBody, Col, Container, Row, Spinner } from 'reactstrap';
import { LogOut } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { LanguageContext } from '../../../Components/Contexts/LanguageContext';
import LanguageDropdown from '../../../Components/Common/LanguageDropdown';
import logoLight from '../../../assets/images/logo-light.png';
import { STORAGE_KEY } from '../../../i18n';
import { benOku, cikisYap, oturumuOku, oturumuSil, tokenCikar, VARSAYILAN_BOSTA_SANIYE } from '../../../services/kisiPanelService';
import KisiPanelGiris from './KisiPanelGiris';
import { Belgelerim, Bilgilerim, EksiklerListesi, KvkkMetinleri, Mesleklerim } from './KisiPanelBolumleri';
import useBostaCikis from './useBostaCikis';

/** <meta> etiketini sayfa açıkken koyar, kapanınca eski haline getirir. */
function useMeta(ad, icerik) {
  useEffect(() => {
    const onceki = document.head.querySelector(`meta[name="${ad}"]`);
    const eskiIcerik = onceki ? onceki.getAttribute('content') : null;
    const el = onceki || document.createElement('meta');
    el.setAttribute('name', ad);
    el.setAttribute('content', icerik);
    if (!onceki) document.head.appendChild(el);
    return () => {
      if (onceki) { if (eskiIcerik === null) onceki.removeAttribute('content'); else onceki.setAttribute('content', eskiIcerik); } else el.remove();
    };
  }, [ad, icerik]);
}

const KisiPanel = () => {
  const { t } = useTranslation('kisiPanel');
  const { selectedLang, setSelectedLang } = useContext(LanguageContext);

  // Anahtar yalnız bir kez ve yalnız bellekte; adres çubuğundan hemen silinir.
  const [token] = useState(() => tokenCikar({ pathname: window.location.pathname, search: window.location.search, hash: window.location.hash }));
  useEffect(() => {
    if (/^\/kisi-panel\/.+/i.test(window.location.pathname) || window.location.search || window.location.hash) {
      window.history.replaceState(null, '', '/kisi-panel');
    }
  }, []);

  useMeta('referrer', 'no-referrer');
  useEffect(() => { document.title = t('baslik'); }, [t]);

  // Dil: seçim yoksa tarayıcının dili; girişten sonra kişinin dili (elle seçilmediyse).
  const [elleSecildi, setElleSecildi] = useState(() => {
    try { return Boolean(localStorage.getItem(STORAGE_KEY)); } catch { return false; }
  });
  useEffect(() => {
    if (!elleSecildi) setSelectedLang(/^tr\b/i.test(navigator.language || '') ? 'tr' : 'en');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const dilSec = useCallback((dil) => { setElleSecildi(true); setSelectedLang(dil); }, [setSelectedLang]);

  const [oturum, setOturum] = useState(() => oturumuOku());
  const [bilgi, setBilgi] = useState(null);                // giriş ekranındaki not: 'bostaCikti' | 'cikti'
  const [ben, setBen] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(Boolean(oturum));
  const [hata, setHata] = useState(null);

  const oturumBitti = useCallback((neden = 'bostaCikti') => {
    oturumuSil();
    setOturum(null);
    setBen(null);
    setBilgi(neden);
  }, []);
  useBostaCikis(oturum ? oturum.bostaSaniye || VARSAYILAN_BOSTA_SANIYE : 0, () => { cikisYap(); oturumBitti('bostaCikti'); }, Boolean(oturum));

  const yenile = useCallback(async () => {
    try {
      const v = await benOku();
      setBen(v);
      setHata(null);
      if (v && v.person && v.person.language && !elleSecildi) setSelectedLang(v.person.language === 'en' ? 'en' : 'tr');
    } catch (e) {
      if (e.oturumBitti) { oturumBitti('bostaCikti'); return; }
      setHata(e.network ? t('hata.baglanti') : t('hata.genel'));
    } finally {
      setYukleniyor(false);
    }
  }, [elleSecildi, oturumBitti, setSelectedLang, t]);

  // KURAL (react-hooks/set-state-in-effect): state, async fonksiyonun içinde değişir.
  useEffect(() => {
    if (!oturum) return undefined;
    let iptal = false;
    async function calistir() {
      try {
        const v = await benOku();
        if (iptal) return;
        setBen(v);
        setHata(null);
        if (v && v.person && v.person.language && !elleSecildi) setSelectedLang(v.person.language === 'en' ? 'en' : 'tr');
      } catch (e) {
        if (iptal) return;
        if (e.oturumBitti) { oturumBitti('bostaCikti'); return; }
        setHata(e.network ? t('hata.baglanti') : t('hata.genel'));
      } finally {
        if (!iptal) setYukleniyor(false);
      }
    }
    calistir();
    return () => { iptal = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oturum]);

  const girildi = () => { setBilgi(null); setYukleniyor(true); setOturum(oturumuOku()); };
  const cikis = async () => { await cikisYap(); oturumBitti('cikti'); };

  const kisi = ben && ben.person ? ben.person : null;

  return (
    <div className="auth-page-wrapper pt-4 pb-4" data-testid="kisi-panel">
      <div className="auth-one-bg-position auth-one-bg" id="auth-particles" style={{ minHeight: 220 }}>
        <div className="bg-overlay"></div>
      </div>
      <div className="position-absolute top-0 end-0 p-3" style={{ zIndex: 10 }}>
        <LanguageDropdown selectedLang={selectedLang} setSelectedLang={dilSec} />
      </div>
      <Container style={{ marginTop: -150, maxWidth: 640 }}>
        <div className="text-center mb-3"><img src={logoLight} alt="" height="64" /></div>

        {!oturum && (
          <Card className="card-bg-fill"><CardBody className="p-4"><KisiPanelGiris token={token} onGirildi={girildi} bilgi={bilgi} /></CardBody></Card>
        )}

        {oturum && (
          <>
            {yukleniyor && !ben && <div className="text-center py-5"><Spinner color="light" /></div>}
            {hata && <Alert color="danger" fade={false} data-testid="panel-hata">{hata}</Alert>}
            {kisi && (
              <>
                <Card className="card-bg-fill">
                  <CardBody>
                    <Row className="align-items-center g-2">
                      <Col>
                        <div className="fw-bold fs-5" data-testid="panel-ad">{t('ana.merhaba', { ad: kisi.ad })}</div>
                        {kisi.firma && <div className="text-muted small">{kisi.firma}</div>}
                        {Array.isArray(kisi.fuarlar) && kisi.fuarlar.length > 0 && (
                          <div className="mt-1">{kisi.fuarlar.map((f) => <Badge key={f.ad} color="light" className="text-body me-1">{f.ad}</Badge>)}</div>
                        )}
                      </Col>
                      <Col xs="auto">
                        <Button color="light" size="sm" id="kp-cikis" onClick={cikis}><LogOut size={14} className="me-1" />{t('ust.cikis')}</Button>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
                <EksiklerListesi liste={ben.checklist || []} />
                <Bilgilerim kisi={kisi} onDegisti={yenile} onOturumBitti={() => oturumBitti('bostaCikti')} />
                <Belgelerim onDegisti={yenile} onOturumBitti={() => oturumBitti('bostaCikti')} />
                <KvkkMetinleri onDegisti={yenile} onOturumBitti={() => oturumBitti('bostaCikti')} />
                <Mesleklerim onDegisti={yenile} onOturumBitti={() => oturumBitti('bostaCikti')} />
              </>
            )}
          </>
        )}
        <p className="text-center text-muted small mt-3">{t('alt')}</p>
      </Container>
    </div>
  );
};

export default KisiPanel;
